import { ActionIcon, Button, Chip, Group, Popover, Stack, Text, VisuallyHidden } from "@mantine/core";
import { ListFilter, RefreshCw } from "lucide-react";
import { useId, useState } from "react";

import type { VoiceAge, VoiceGender } from "../../api/generated/model";
import classes from "./voices.module.css";

/** Live's filters on the global voices: none set by default. */
export interface VoiceFilter {
  gender: VoiceGender | null;
  age: VoiceAge | null;
}

export const NO_FILTER: VoiceFilter = { gender: null, age: null };

/** Live's options and words (`constants/global-filters.ts`, `constants/avatar.tsx`). */
const GENDERS: { value: VoiceGender; label: string }[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
];
const AGES: { value: VoiceAge; label: string }[] = [
  { value: "kid", label: "Kid" },
  { value: "young_adult", label: "Young Adult" },
  { value: "adult", label: "Adult" },
  { value: "senior", label: "Senior" },
];

export interface VoiceFiltersProps {
  applied: VoiceFilter;
  onApply: (filter: VoiceFilter) => void;
}

/**
 * Live's filter on the global voices (`voice-filters-popover`, capture 12c): the icon, bordered
 * while a filter is set, opens Gender and Age to the left; Apply sets them and closes, Reset
 * clears them and closes. Nothing applies before Apply, and a choice is unset only by Reset.
 */
export function VoiceFilters({ applied, onApply }: VoiceFiltersProps) {
  const [opened, setOpened] = useState(false);
  // What the popover shows: the applied filter each time it opens.
  const [draft, setDraft] = useState<VoiceFilter>(applied);
  const titleId = useId();
  const apply = (filter: VoiceFilter) => {
    onApply(filter);
    setOpened(false);
  };
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={320}
      position="left-start"
      shadow="md"
      trapFocus
      returnFocus
    >
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          size="md"
          ml="auto"
          className={classes.filter}
          data-active={applied.gender !== null || applied.age !== null || undefined}
          aria-label="Toggle voice filters"
          onClick={() => {
            setDraft(applied);
            setOpened((open) => !open);
          }}
        >
          <ListFilter size={20} strokeWidth={1.5} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown aria-labelledby={titleId}>
        <VisuallyHidden id={titleId}>Voice filters</VisuallyHidden>
        <Stack gap="md">
          <Stack gap={8}>
            <Text size="sm" fw={500}>
              Gender
            </Text>
            <Chip.Group
              multiple={false}
              value={draft.gender ?? ""}
              onChange={(value) => setDraft({ ...draft, gender: value as VoiceGender })}
            >
              <Group gap={8} role="radiogroup" aria-label="Gender">
                {GENDERS.map((each) => (
                  <Chip key={each.value} value={each.value} type="radio">
                    {each.label}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Stack>
          <Stack gap={8}>
            <Text size="sm" fw={500}>
              Age
            </Text>
            <Chip.Group
              multiple={false}
              value={draft.age ?? ""}
              onChange={(value) => setDraft({ ...draft, age: value as VoiceAge })}
            >
              <Group gap={8} role="radiogroup" aria-label="Age">
                {AGES.map((each) => (
                  <Chip key={each.value} value={each.value} type="radio">
                    {each.label}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Stack>
          <Group grow gap={8}>
            <Button onClick={() => apply(draft)}>Apply</Button>
            <Button
              variant="default"
              leftSection={<RefreshCw size={16} />}
              onClick={() => {
                setDraft(NO_FILTER);
                apply(NO_FILTER);
              }}
            >
              Reset
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
